import React, { useState, useEffect } from 'react';
import { db } from '../utils/FirebaseConfig';
import {
    collection,
    doc,
    deleteDoc,
    onSnapshot,
    query,
    orderBy,
    limit,
    serverTimestamp,
    Timestamp,
} from "firebase/firestore";
import { saveAnnouncement } from "../services/mosqueContentRepository";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import '../App.css';

const Message = ({ isMobile }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [currentMessageId, setCurrentMessageId] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [messageType, setMessageType] = useState('announcement');
    const [funeral, setFuneral] = useState({
        deceasedName: '',
        gender: 'male',
        date: new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Asia/Kuala_Lumpur',
        }).format(new Date()),
        timeMode: 'after_prayer',
        afterPrayer: 'zohor',
        time: '13:30',
        location: '',
        note: '',
    });

    const userProfile = JSON.parse(localStorage.getItem('userProfile') || "{}");
    const mosqueId = userProfile?.mosqueId || "";
    const mosqueName = String(
        userProfile?.namaMasjid ||
        userProfile?.masjidName ||
        userProfile?.mosqueName ||
        userProfile?.profail?.nama ||
        ''
    ).trim();

    const prayerLabels = {
        subuh: 'Subuh',
        zohor: 'Zohor',
        asar: 'Asar',
        maghrib: 'Maghrib',
        isyak: 'Isyak',
    };

    const updateFuneral = (field, value) => {
        setFuneral((current) => ({ ...current, [field]: value }));
    };

    const funeralDate = (date, time = '12:00') =>
        new Date(`${date}T${time}:00+08:00`);

    const buildFuneralText = (data) => {
        if (!data.date) return 'Pilih tarikh untuk melihat pratonton.';
        const name = data.deceasedName.trim();
        const honorific = data.gender === 'female' ? 'Allahyarhamah' : 'Allahyarham';
        const person = name ? ` bagi ${honorific} ${name}` : '';
        const dateLabel = new Intl.DateTimeFormat('ms-MY', {
            timeZone: 'Asia/Kuala_Lumpur',
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        }).format(funeralDate(data.date));
        const timeLabel = data.timeMode === 'after_prayer'
            ? `selepas solat ${prayerLabels[data.afterPrayer]}`
            : `pukul ${new Intl.DateTimeFormat('ms-MY', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: 'Asia/Kuala_Lumpur',
            }).format(funeralDate(data.date, data.time))}`;
        const location = data.location.trim() || mosqueName;
        const note = data.note.trim() ? ` ${data.note.trim()}` : '';

        return `Solat jenazah${person} akan diadakan pada ${dateLabel}, ${timeLabel}${location ? ` di ${location}` : ''}.${note}`;
    };

    const funeralPayload = () => {
        const location = funeral.location.trim() || mosqueName;
        const scheduledDate = funeralDate(
            funeral.date,
            funeral.timeMode === 'specific_time' ? funeral.time : '12:00'
        );
        const expiry = funeralDate(funeral.date, '23:59');

        return {
            type: 'funeral_prayer',
            title: mosqueName || 'Solat Jenazah',
            masjidName: mosqueName || null,
            text: buildFuneralText({ ...funeral, location }),
            deceasedName: funeral.deceasedName.trim() || null,
            gender: funeral.gender,
            funeralDate: Timestamp.fromDate(funeralDate(funeral.date)),
            dateYMD: funeral.date,
            timeMode: funeral.timeMode,
            afterPrayer: funeral.timeMode === 'after_prayer' ? funeral.afterPrayer : null,
            funeralTime: funeral.timeMode === 'specific_time' ? funeral.time : null,
            timeLabel: funeral.timeMode === 'after_prayer'
                ? `Selepas ${prayerLabels[funeral.afterPrayer]}`
                : funeral.time,
            location,
            note: funeral.note.trim() || null,
            scheduledAt: Timestamp.fromDate(scheduledDate),
            expiresAt: Timestamp.fromDate(expiry),
        };
    };

    if (!mosqueId) {
        console.warn("⚠️ Tiada mosqueId dalam userProfile. Sila semak login flow.");
    }

    useEffect(() => {
        if (searchParams.get('new') !== '1') return;

        const requestedType = searchParams.get('type');
        setMessageType(requestedType === 'funeral_prayer' ? 'funeral_prayer' : 'announcement');
        setIsEditing(false);
        setCurrentMessageId(null);
        setShowEditor(true);

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('new');
        nextParams.delete('type');
        setSearchParams(nextParams, { replace: true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!mosqueId) return;

        const announcementsRef = collection(
            db,
            'masajid',
            mosqueId,
            'announcements'
        );

        // Senarai ini hanya memaparkan rekod terkini. Tanpa limit, setiap
        // pembukaan halaman membaca seluruh sejarah pengumuman.
        const q = query(
            announcementsRef,
            orderBy('createdAt', 'desc'),
            limit(100)
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const list = snapshot.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                }));

                setMessages(list);
            },
            (error) => {
                console.error("❌ Error loading announcements:", error);
                toast.error('Gagal memuatkan pengumuman');
            }
        );

        return () => unsubscribe();
    }, [mosqueId]);

    const resetEditor = () => {
        setNewMessage('');
        setMessageType('announcement');
        setFuneral((current) => ({
            ...current,
            deceasedName: '',
            location: '',
            note: '',
        }));
        setIsEditing(false);
        setCurrentMessageId(null);
        setShowEditor(false);
    };

    const handleAddFuneral = async () => {
        if (!mosqueId || !funeral.date || !funeral.gender) return;
        setIsSubmitting(true);

        try {
            await saveAnnouncement(mosqueId, {
                ...funeralPayload(),
                status: 'published',
                sendPush: true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                createdBy: userProfile?.uid || userProfile?.id || null,
                createdByName: userProfile?.name || userProfile?.nama || null,
            });
            resetEditor();
            toast.success('Pemakluman solat jenazah telah dihantar');
        } catch (error) {
            console.error('❌ Error adding funeral announcement:', error);
            toast.error('Gagal menghantar pemakluman solat jenazah');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddMessage = async () => {
        const text = newMessage.trim();

        if (!text || !mosqueId) return;

        setIsSubmitting(true);

        try {
            await saveAnnouncement(mosqueId, {
                title: mosqueName || 'Pengumuman',
                masjidName: mosqueName || null,
                text,
                status: 'published',
                sendPush: true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                createdBy: userProfile?.uid || userProfile?.id || null,
                createdByName: userProfile?.name || userProfile?.nama || null,
            });

            resetEditor();
            toast.success('Pengumuman berjaya ditambah');
        } catch (error) {
            console.error("❌ Error adding announcement:", error);
            toast.error('Gagal menambah pengumuman');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateMessage = async () => {
        const text = newMessage.trim();

        if (!text || !mosqueId || !currentMessageId) return;

        setIsSubmitting(true);

        try {
            await saveAnnouncement(mosqueId, {
                title: mosqueName || 'Pengumuman',
                masjidName: mosqueName || null,
                text,
                updatedAt: serverTimestamp(),
            }, { id: currentMessageId });

            resetEditor();
            toast.success('Pengumuman berjaya dikemaskini');
        } catch (error) {
            console.error("❌ Error updating announcement:", error);
            toast.error('Gagal kemaskini pengumuman');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteMessage = async (messageId) => {
        if (!mosqueId || !messageId) return;

        const confirmDelete = window.confirm(
            'Padam pengumuman ini? Tindakan ini tidak boleh dibuat asal.'
        );

        if (!confirmDelete) return;

        try {
            const messageRef = doc(
                db,
                'masajid',
                mosqueId,
                'announcements',
                messageId
            );

            await deleteDoc(messageRef);

            toast.success('Pengumuman berjaya dipadam');
        } catch (error) {
            console.error("❌ Error deleting announcement:", error);
            toast.error('Gagal padam pengumuman');
        }
    };

    const handleEditMessage = (message) => {
        if (message.type === 'funeral_prayer') {
            setMessageType('funeral_prayer');
            setFuneral({
                deceasedName: message.deceasedName || '',
                gender: message.gender || 'male',
                date: message.dateYMD || new Intl.DateTimeFormat('en-CA', {
                    timeZone: 'Asia/Kuala_Lumpur',
                }).format(message.funeralDate?.toDate?.() || new Date()),
                timeMode: message.timeMode || 'after_prayer',
                afterPrayer: message.afterPrayer || 'zohor',
                time: message.funeralTime || '13:30',
                location: message.location || '',
                note: message.note || '',
            });
        } else {
            setMessageType('announcement');
            setNewMessage(message.text || '');
        }
        setIsEditing(true);
        setCurrentMessageId(message.id);
        setShowEditor(true);
    };

    const handleUpdateFuneral = async () => {
        if (!mosqueId || !currentMessageId) return;
        setIsSubmitting(true);
        try {
            await saveAnnouncement(
                mosqueId,
                { ...funeralPayload(), updatedAt: serverTimestamp() },
                { id: currentMessageId }
            );
            resetEditor();
            toast.success('Pemakluman solat jenazah dikemaskini');
        } catch (error) {
            console.error('❌ Error updating funeral announcement:', error);
            toast.error('Gagal mengemaskini pemakluman');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatCreatedAt = (createdAt) => {
        if (!createdAt) return '';

        if (createdAt?.seconds) {
            return new Date(createdAt.seconds * 1000).toLocaleString();
        }

        if (createdAt?.toDate) {
            return createdAt.toDate().toLocaleString();
        }

        return new Date(createdAt).toLocaleString();
    };

    if (!mosqueId) {
        return (
            <div style={{ padding: 20 }}>
                ⚠️ Sila log masuk semula. Tiada ID masjid ditemui.
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <h3>Pengumuman Masjid</h3>

                {!showEditor && (
                    <button
                        className='button'
                        onClick={() => setShowEditor(true)}
                    >
                        Tambah
                    </button>
                )}
            </div>

            {showEditor && (
                <div className="message-editor">
                    {!isEditing && (
                        <div className="message-type-tabs">
                            <button
                                type="button"
                                className={messageType === 'announcement' ? 'active' : ''}
                                onClick={() => setMessageType('announcement')}
                            >
                                Pengumuman biasa
                            </button>
                            <button
                                type="button"
                                className={messageType === 'funeral_prayer' ? 'active' : ''}
                                onClick={() => setMessageType('funeral_prayer')}
                            >
                                Solat jenazah
                            </button>
                        </div>
                    )}

                    {messageType === 'announcement' ? (
                        <textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Tulis pengumuman di sini..."
                            rows="4"
                        />
                    ) : (
                        <div className="funeral-form">
                            <div className="form-field form-field-wide">
                                <label>Nama jenazah <span>(pilihan)</span></label>
                                <input
                                    value={funeral.deceasedName}
                                    onChange={(e) => updateFuneral('deceasedName', e.target.value)}
                                    placeholder="Contoh: Ahmad bin Abdullah"
                                />
                            </div>
                            <div className="form-field">
                                <label>Jantina</label>
                                <select value={funeral.gender} onChange={(e) => updateFuneral('gender', e.target.value)}>
                                    <option value="male">Lelaki</option>
                                    <option value="female">Perempuan</option>
                                </select>
                            </div>
                            <div className="form-field">
                                <label>Tarikh</label>
                                <input type="date" value={funeral.date} onChange={(e) => updateFuneral('date', e.target.value)} />
                            </div>
                            <div className="form-field">
                                <label>Waktu</label>
                                <select value={funeral.timeMode} onChange={(e) => updateFuneral('timeMode', e.target.value)}>
                                    <option value="after_prayer">Selepas waktu solat</option>
                                    <option value="specific_time">Masa khusus</option>
                                </select>
                            </div>
                            <div className="form-field">
                                <label>{funeral.timeMode === 'after_prayer' ? 'Selepas solat' : 'Masa'}</label>
                                {funeral.timeMode === 'after_prayer' ? (
                                    <select value={funeral.afterPrayer} onChange={(e) => updateFuneral('afterPrayer', e.target.value)}>
                                        {Object.entries(prayerLabels).map(([value, label]) => (
                                            <option value={value} key={value}>{label}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input type="time" value={funeral.time} onChange={(e) => updateFuneral('time', e.target.value)} />
                                )}
                            </div>
                            <div className="form-field form-field-wide">
                                <label>Lokasi</label>
                                <input value={funeral.location} onChange={(e) => updateFuneral('location', e.target.value)} placeholder={mosqueName || 'Nama masjid / surau'} />
                            </div>
                            <div className="form-field form-field-wide">
                                <label>Catatan <span>(pilihan)</span></label>
                                <textarea rows="2" value={funeral.note} onChange={(e) => updateFuneral('note', e.target.value)} placeholder="Contoh: Jemaah diminta hadir 15 minit lebih awal" />
                            </div>
                            <div className="funeral-preview">
                                <strong>Pratonton</strong>
                                <p>{buildFuneralText({ ...funeral, location: funeral.location || mosqueName })}</p>
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'flex', marginTop: '10px', alignItems: 'center' }}>
                        <button
                            className='button'
                            disabled={isSubmitting}
                            onClick={() => {
                                if (messageType === 'funeral_prayer') {
                                    if (isEditing && currentMessageId) handleUpdateFuneral();
                                    else handleAddFuneral();
                                } else if (isEditing && currentMessageId) {
                                    handleUpdateMessage();
                                } else {
                                    handleAddMessage();
                                }
                            }}
                        >
                            {isSubmitting
                                ? 'Menyimpan...'
                                : isEditing
                                    ? 'Kemaskini'
                                    : 'Hantar'}
                        </button>

                        <div
                            style={{
                                cursor: 'pointer',
                                marginLeft: '10px',
                                textDecoration: 'none',
                                color: '#7A1777',
                            }}
                            onClick={resetEditor}
                        >
                            Batal
                        </div>
                    </div>
                </div>
            )}

            <div className="list-message">
                {messages.length === 0 ? (
                    <p>Tiada pengumuman</p>
                ) : (
                    messages.map((message) => (
                        <div key={message.id} className="message-item">
                            {message.type === 'funeral_prayer' && (
                                <span className="funeral-label">Solat Jenazah</span>
                            )}
                            <p>{message.text}</p>

                            {message.createdAt && (
                                <small>{formatCreatedAt(message.createdAt)}</small>
                            )}

                            <div className="message-actions">
                                <FaEdit onClick={() => handleEditMessage(message)} />
                                <FaTrashAlt onClick={() => handleDeleteMessage(message.id)} />
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default Message;
